import React, { useState, useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import AuthGate from './components/AuthGate'
import Landing from './components/Landing'
import ProductsIndex from './components/ProductsIndex'
import ProductPage from './components/ProductPage'
import UseCasesIndex from './components/UseCasesIndex'
import Contact from './components/Contact'
import UseCasePage from './components/UseCasePage'
import SignIn from './pages/SignIn'
import SignUp from './pages/SignUp'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Cart from './pages/Cart'
import Layout from './components/Layout'
import { getSession } from './lib/session'
import RequireAdmin from './components/RequireAdmin'
import Dashboard from './pages/Dashboard'
import Services from './pages/Services'
import Guide from './pages/Guide'
import TenantSetup from './pages/TenantSetup'
import ChatbotManager from './pages/ChatbotManager'
import SyncManager from './pages/SyncManager'
import WorkflowBuilder from './pages/WorkflowBuilder'
import AdapterManager from './pages/AdapterManager'
import UsageBilling from './pages/UsageBilling'
import Docs from './pages/Docs'
import Checkout from './pages/Checkout'
import Onboarding from './pages/Onboarding'
import Instances from './pages/Instances'
import IntakeManager from './pages/IntakeManager'
import RagManager from './pages/RagManager'
import Admin from './pages/Admin'
import Coupons from './pages/Coupons'
import Governance from './pages/Governance'
import Agents from './pages/Agents'
import Campaigns from './pages/Campaigns'
import Contacts from './pages/Contacts'
import Templates from './pages/Templates'
import Inbox from './pages/Inbox'
import MarketingBrain from './pages/MarketingBrain'
import GrowthLab from './pages/GrowthLab'
import MarketingSeoStudio from './pages/MarketingSeoStudio'
import SeoIntelligence from './pages/SeoIntelligence'
import ContentStudio from './pages/ContentStudio'
import SiteContent from './pages/SiteContent'
import Analytics from './pages/Analytics'
import Commerce from './pages/Commerce'
import Reseller from './pages/Reseller'
import Integrations from './pages/Integrations'
import Connections from './pages/Connections'
import NotificationsSettings from './pages/NotificationsSettings'

// Re-render the root wrapper when the platform session changes (login / logout),
// so `/` flips between the marketing homepage and the console without a reload.
function useIsAuthed() {
  const [authed, setAuthed] = useState(() => !!getSession()?.apiKey)
  useEffect(() => {
    const sync = () => setAuthed(!!getSession()?.apiKey)
    window.addEventListener('reright-session', sync)
    return () => window.removeEventListener('reright-session', sync)
  }, [])
  return authed
}

// The gated console — every non-public route. Logged OUT at `/` shows the
// marketing homepage (NO embedded sign-in); logged out anywhere else is bounced
// to /signin by <AuthGate>; logged in gets the full console.
function GatedApp() {
  const authed = useIsAuthed()
  const { pathname } = useLocation()

  // Logged-out root → the marketing homepage on its own (no auth card).
  if (!authed && pathname === '/') return <Landing />

  return (
    <AuthGate>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Services />} />
          <Route path="instances" element={<Instances />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="setup" element={<TenantSetup />} />
          <Route path="answers" element={<ChatbotManager />} />
          <Route path="intake" element={<IntakeManager />} />
          <Route path="sync" element={<SyncManager />} />
          <Route path="rag" element={<RagManager />} />
          <Route path="workflows" element={<WorkflowBuilder />} />
          <Route path="agents" element={<Agents />} />
          <Route path="adapters" element={<AdapterManager />} />
          <Route path="governance" element={<Governance />} />
          <Route path="billing" element={<UsageBilling />} />
          <Route path="docs" element={<Docs />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="welcome" element={<Onboarding />} />
          <Route path="admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
          <Route path="site-content" element={<RequireAdmin><SiteContent /></RequireAdmin>} />
          <Route path="coupons" element={<RequireAdmin><Coupons /></RequireAdmin>} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="templates" element={<Templates />} />
          <Route path="inbox" element={<Inbox />} />
          <Route path="marketing" element={<MarketingBrain />} />
          <Route path="growth-lab" element={<GrowthLab />} />
          <Route path="studio" element={<MarketingSeoStudio />} />
          <Route path="seo-intelligence" element={<SeoIntelligence />} />
          <Route path="content" element={<ContentStudio />} />
          <Route path="commerce" element={<Commerce />} />
          <Route path="reseller" element={<Reseller />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="connections" element={<Connections />} />
          <Route path="notifications" element={<NotificationsSettings />} />
        </Route>
      </Routes>
    </AuthGate>
  )
}

function App() {
  return (
    <>
      <Toaster position="top-right" />
      <Routes>
        {/* Public marketing — no login required */}
        <Route path="/home" element={<Landing />} />
        <Route path="/products" element={<ProductsIndex />} />
        <Route path="/products/:slug" element={<ProductPage />} />
        <Route path="/use-cases" element={<UseCasesIndex />} />
        <Route path="/use-cases/:slug" element={<UseCasePage />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/contact" element={<Contact />} />

        {/* Dedicated authentication pages — public, consistently dark-themed */}
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Root + everything else: marketing home when logged out, console when in */}
        <Route path="/*" element={<GatedApp />} />
      </Routes>
    </>
  )
}

export default App
